import { queryOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { UserFeature, UserFeatureList } from 'src/models/user.models'
import {
  BrandRedirectResponse,
  getUserFeatures,
  postBrandCheck,
} from 'src/queries/auth.network'
import { authQueryKeys } from 'src/queries/queries.constants'
import { ExtendBaseQueryOptions } from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectIsAuthenticatedFromState } from 'src/redux/selectors/auth.selectors'

// Re-export so existing `import { BrandRedirectResponse } from '.../auth.queries'`
// call sites keep resolving after the type moved to auth.network.ts.
export type { BrandRedirectResponse } from 'src/queries/auth.network'

interface QueryUserFeaturesOptions
  extends ExtendBaseQueryOptions<
    { features: UserFeatureList },
    UserFeatureList
  > {}

// queryOptions() factory reuses the existing authQueryKeys.features key; network
// functions live in auth.network.ts. The brand-suggestions read stays inlined in
// its hook (plain ['brand_suggestions'] key) because spreading a branded
// queryOptions() result under the caller's `...options` widens TQueryKey and
// breaks useQuery's overload resolution.
export const authQueries = {
  features: (
    features: UserFeature[],
    identity?: { userId?: string; organizationId?: string },
  ) =>
    queryOptions({
      queryKey: authQueryKeys.features(features, identity),
      queryFn: () => getUserFeatures(features),
    }),
}

export const useQueryUserFeatures = (
  features: UserFeature[],
  options?: QueryUserFeaturesOptions,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })
  const hasOrganization = !!user?.organizationId
  const { queryCacheOptions, ...restOptions } = options ?? {}

  return useQuery({
    ...authQueries.features(features, {
      userId: user?.id,
      organizationId: user?.organizationId,
    }),
    select: (data) => data.features,
    ...queryCacheOptions,
    ...restOptions,
    enabled: hasOrganization && (options?.enabled ?? true),
  })
}

export const useQueryBrandRedirectSuggestions = (
  options?: Partial<UseQueryOptions<BrandRedirectResponse[]>>,
) => {
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const { data: user } = useQueryOwnUser({ enabled: isAuthenticated })

  return useQuery<BrandRedirectResponse[]>({
    ...options,
    enabled: user?.email !== undefined && (options?.enabled ?? true),
    queryKey: ['brand_suggestions'],
    queryFn: () => postBrandCheck(user?.email ?? ''),
  })
}
